import React, { useContext, useEffect, useRef, useMemo } from 'react';
import PropType from 'prop-types';
import { useRouter } from 'next/router';
import cx from 'utils/classnames';
import camelCase from 'lodash/camelCase';
import { capitalizeFirstLetter } from 'utils/stringHelpers';
import GlobalContext from 'hooks/contexts/GlobalContext';
import { ROUTE_TO_CONTENT_TYPE_MAP } from 'consts';

function ThemeWrapper({
  microSiteTheme = 'default-theme',
  children,
  title,
  className = '',
}) {
  const {
    windowSize,
    flags,
    resumeModal,
    activeModalId,
    cssCustomProps,
    actions,
    activeExperiment,
    microPortal: { submittedSchools },
    siteMeta: { clickPortalSettings },
  } = useContext(GlobalContext);

  const themeWrapperRef = useRef(null);
  const router = useRouter();

  useEffect(() => {
    actions.updateGlobalRefs({
      themeWrapperRef,
    });
    return () => {
      actions.updateGlobalRefs({
        themeWrapperRef: null,
      });
    };
  }, []);

  const pageType = ROUTE_TO_CONTENT_TYPE_MAP[router?.route];

  const experimentClassName = useMemo(
    () =>
      activeExperiment.reduce((acc, [, variant]) => {
        if (variant.settings.globalContainerClass) {
          acc[variant.settings.globalContainerClass] = true;
        }
        return acc;
      }, {}),
    [activeExperiment]
  );

  const pageClassName = cx({
    [`page--${className}`]: !!className,
    'page--home': !title,
    'page--isLoading': flags.isLoading,
    [`page--${camelCase(title)}`]: title,
    [`page--is${capitalizeFirstLetter(pageType)}Page`]: pageType,
    [`page--is${capitalizeFirstLetter(windowSize.currentBreakpoint)}`]: true,
    'page--formIsDirty': flags.isFormDirty,
    'page--formIsFirstStep': flags.isFirstStep,
    'page--formIsSecondStep': flags.isSecondStep,
    'page--isSubmitting': flags.isSubmitting,
    'page--userHasInteractedWithForm':
      (!flags.isFirstStep && flags.isFormDirty) ||
      flags.isResumeSessionAcknowledged,
    'page--hasStickyHeader': flags.hasStickyHeader,
    'page--confirmationModalIsVisible': resumeModal.isOpen,
    'page--modalIsVisible': !!activeModalId,
    'page--navIsOpen': flags.navIsOpen,
    'page--hasFormSidebar': flags.hasFormSidebar,
    'page--isResultsPage': flags.isResultsPage,
    'page--userHasSubmittedForm': flags.isThankYouPage,
    'page--hasSubmittedSchools': submittedSchools.length > 0,
    'page--isClickPortalMultiRow': clickPortalSettings?.isMultiRow,
    ...experimentClassName,
  });

  return (
    <div
      id={microSiteTheme}
      data-testid="themeWrapper"
      className={pageClassName}
      style={cssCustomProps}
      ref={themeWrapperRef}
    >
      {children}
    </div>
  );
}

ThemeWrapper.propTypes = {
  microSiteTheme: PropType.string,
  title: PropType.string,
  className: PropType.string,
  children: PropType.node.isRequired,
};

export default ThemeWrapper;
